import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from 'recharts';
import { formatCurrency } from '../utils/taxCalculations';
import './TaxBreakdownChart.css';

function TaxBreakdownChart({ taxBandData, calculation, simulationResult }) {
  // Prepare data for bar chart showing tax paid per band
  const barChartData = taxBandData
    .filter(band => band.taxPaid > 0 || band.isActive)
    .map(band => ({
      name: band.name,
      amount: band.amount,
      taxPaid: band.taxPaid,
      rate: band.rate,
      fill: band.fill,
    }));

  // Prepare data for pie chart showing overall breakdown
  const pieChartData = [
    {
      name: 'Take Home',
      value: calculation.takeHome,
      fill: '#4CAF50',
    },
    {
      name: 'Income Tax',
      value: calculation.incomeTax.totalTax,
      fill: '#2196F3',
    },
    {
      name: 'National Insurance',
      value: calculation.nationalInsurance.totalNI,
      fill: '#FF9800',
    },
  ].filter(item => item.value > 0);

  // Comparison data for simulation
  const comparisonData = simulationResult ? [
    {
      name: 'Current',
      takeHome: calculation.takeHome,
      tax: calculation.incomeTax.totalTax,
    },
    {
      name: 'With Changes',
      takeHome: simulationResult.simulated.takeHome,
      tax: simulationResult.simulated.totalTax,
    },
  ] : null;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null; // Don't show label if less than 5%

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="tax-breakdown-chart">
      <div className="charts-grid">
        {/* Pie Chart - Overall Breakdown */}
        <div className="chart-card">
          <h3>Where Your Money Goes</h3>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={100}
                  dataKey="value"
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bar Chart - Tax by Band */}
        <div className="chart-card">
          <h3>Tax Paid by Band</h3>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={barChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12 }}
                  angle={-15}
                  textAnchor="end"
                  height={60}
                />
                <YAxis 
                  tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="taxPaid" name="Tax Paid" radius={[4, 4, 0, 0]}>
                  {barChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Comparison Chart - When Simulation Active */}
      {comparisonData && (
        <div className="chart-card comparison-chart">
          <h3>Impact of Policy Changes</h3>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart 
                data={comparisonData} 
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                barCategoryGap="30%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="takeHome" name="Take Home" fill="#4CAF50" radius={[4, 4, 0, 0]} />
                <Bar dataKey="tax" name="Income Tax" fill="#2196F3" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="savings-highlight">
            <span className="savings-label">You could take home an extra</span>
            <span className="savings-amount">{formatCurrency(simulationResult.savings.annual)}/year</span>
            <span className="savings-monthly">({formatCurrency(simulationResult.savings.monthly)}/month)</span>
          </div>
        </div>
      )}

      {/* Tax Band Reference */}
      <div className="tax-bands-reference">
        <h3>UK Tax Bands 2025-2026</h3>
        <div className="bands-list">
          {taxBandData.map((band, index) => (
            <div 
              key={index} 
              className={`band-item ${band.isActive ? 'active' : ''}`}
              style={{ borderLeftColor: band.fill }}
            >
              <div className="band-info">
                <span className="band-name">{band.name}</span>
                <span className="band-range">{band.range}</span>
              </div>
              <div className="band-rate">{band.rate}</div>
              {band.isActive && band.taxPaid > 0 && (
                <div className="band-paid">
                  Tax: {formatCurrency(band.taxPaid)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default TaxBreakdownChart;
